// plugins/withExcelPaste.ts
import { Editor, Transforms, Descendant, Element, Path, Node, Range } from 'slate';
import { ReactEditor } from 'slate-react';
import { HistoryEditor } from 'slate-history';

interface TableCellNode {
    type: 'table-cell' | 'table-header';
    children: Descendant[];
}

interface TableRowNode {
    type: 'table-row';
    children: TableCellNode[];
}

interface TableNode {
    type: 'table' | 'tableWithoutHeaders';
    children: TableRowNode[];
    rows?: number; // Added for metadata
    columns?: number; // Added for metadata
}

/**
 * Sanitizes HTML content to plain text.
 */
function sanitizeContent(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
}

/**
 * Parses a DOM table into a 2D array of cell text values.
 */
function parseTableTo2DArray(tableElement: HTMLTableElement): string[][] {
    const rows: string[][] = [];
    Array.from(tableElement.querySelectorAll('tr')).forEach((row) => {
        const cells: string[] = [];
        Array.from(row.querySelectorAll('td, th')).forEach((cell) => {
            cells.push(sanitizeContent(cell.innerHTML));
        });
        if (cells.length > 0) {
            rows.push(cells);
        }
    });
    return rows;
}

/**
 * A helper to transform the DOM <table> into Slate node structure.
 */
function createTableNodeFromDom(tableElement: HTMLTableElement): TableNode {
    const rowNodes: TableRowNode[] = Array.from(tableElement.querySelectorAll('tr')).map((row) => {
        const cellNodes: TableCellNode[] = Array.from(row.querySelectorAll('td, th')).map((cell) => {
            // Handle nested tables
            if (cell.querySelector('table')) {
                console.warn('Nested tables are not supported and will be flattened');
            }

            return {
                type: 'table-cell',
                children: [
                    {
                        type: 'paragraph',
                        children: [{ text: sanitizeContent(cell.innerHTML) }],
                    },
                ],
            };
        });
        if (cellNodes.length === 0) {
            console.warn('Empty table row detected');
        }
        return {
            type: 'table-row',
            children: cellNodes,
        };
    });

    // Calculate and set rows/columns metadata
    const numRows = rowNodes.length;
    const numColumns = Math.max(...rowNodes.map((row) => row.children.length), 0);

    return {
        type: 'table',
        children: rowNodes,
        rows: numRows,
        columns: numColumns,
    };
}

/**
 * Creates a new table row with empty cells.
 * @param numCells - Number of cells in the row
 * @param isHeader - Whether this is a header row
 */
function createEmptyRow(numCells: number, isHeader: boolean = false): TableRowNode {
    const cells: TableCellNode[] = [];
    for (let i = 0; i < numCells; i++) {
        cells.push({
            type: isHeader ? 'table-header' : 'table-cell',
            children: [{ type: 'paragraph', children: [{ text: '' }] } as any],
        });
    }
    return { type: 'table-row', children: cells };
}

/**
 * Creates an empty table cell.
 * @param isHeader - Whether this is a header cell
 */
function createEmptyCell(isHeader: boolean = false): TableCellNode {
    return {
        type: isHeader ? 'table-header' : 'table-cell',
        children: [{ type: 'paragraph', children: [{ text: '' }] } as any],
    };
}

/**
 * Spreads pasted table data into an existing table starting from the current cell.
 * Automatically expands the table if pasted data exceeds current dimensions.
 * @param editor - Slate editor instance
 * @param clipboardData - 2D array of cell text values from clipboard
 * @param cellPath - Path to the currently selected cell
 * @param tablePath - Path to the parent table
 * @param tableNode - The parent table node
 */
function spreadDataIntoTable(editor: Editor, clipboardData: string[][], cellPath: Path, tablePath: Path, tableNode: TableNode): void {
    const startRowIndex = cellPath[cellPath.length - 2]; // Row index within table
    const startCellIndex = cellPath[cellPath.length - 1]; // Cell index within row

    const currentRowCount = tableNode.children.length;
    const currentColCount = tableNode.children[0]?.children.length || 0;

    // Find the maximum column count across all clipboard rows
    const maxClipboardCols = Math.max(...clipboardData.map((row) => row.length), 0);

    const neededRows = startRowIndex + clipboardData.length;
    const neededCols = startCellIndex + maxClipboardCols;

    const rowsToAdd = Math.max(0, neededRows - currentRowCount);
    const colsToAdd = Math.max(0, neededCols - currentColCount);

    Editor.withoutNormalizing(editor, () => {
        // Step 1: Add new columns to ALL existing rows (if needed)
        if (colsToAdd > 0) {
            for (let rowIdx = 0; rowIdx < currentRowCount; rowIdx++) {
                const isHeaderRow = rowIdx === 0;
                for (let c = 0; c < colsToAdd; c++) {
                    const insertPath = [...tablePath, rowIdx, currentColCount + c];
                    Transforms.insertNodes(editor, createEmptyCell(isHeaderRow), { at: insertPath });
                }
            }
        }

        // Step 2: Add new rows at the end (if needed)
        if (rowsToAdd > 0) {
            const totalCols = currentColCount + colsToAdd;
            for (let r = 0; r < rowsToAdd; r++) {
                const insertPath = [...tablePath, currentRowCount + r];
                Transforms.insertNodes(editor, createEmptyRow(totalCols, false), { at: insertPath });
            }
        }

        // Step 3: Now populate ALL cells with the clipboard data
        // We iterate in reverse order to avoid path invalidation issues
        for (let rowOffset = clipboardData.length - 1; rowOffset >= 0; rowOffset--) {
            const rowData = clipboardData[rowOffset];
            const targetRowIndex = startRowIndex + rowOffset;

            for (let colOffset = rowData.length - 1; colOffset >= 0; colOffset--) {
                const cellText = rowData[colOffset];
                const targetCellIndex = startCellIndex + colOffset;

                // Path to the paragraph inside the cell
                const paragraphPath = [...tablePath, targetRowIndex, targetCellIndex, 0];

                try {
                    if (Node.has(editor, paragraphPath)) {
                        // Remove the existing paragraph and insert a new one with the text
                        Transforms.removeNodes(editor, { at: paragraphPath });
                        Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: cellText }] } as any, { at: paragraphPath });
                    }
                } catch (error) {
                    // Failed to paste at cell
                }
            }
        }

        // Update table metadata (rows/columns count) if it exists
        try {
            const finalRowCount = Math.max(currentRowCount, neededRows);
            const finalColCount = Math.max(currentColCount, neededCols);
            Transforms.setNodes(editor, { rows: finalRowCount, columns: finalColCount } as any, { at: tablePath });
        } catch (e) {
            // Table may not have these properties, ignore
        }
    });

    // Reset selection to first pasted cell
    try {
        const firstCellPath = [...tablePath, startRowIndex, startCellIndex, 0, 0];
        if (Node.has(editor, firstCellPath)) {
            Transforms.select(editor, Editor.end(editor, firstCellPath));
        }
    } catch (e) {
        // Ignore selection errors
    }
}

/**
 * A plugin that intercepts the `insertData` method to detect if the clipboard
 * contains a table (e.g., from Excel) and handles:
 * 1. Pasting into an existing table cell: spreads data across cells
 * 2. Pasting outside a table: inserts a new table
 */
function withExcelPaste<T extends Editor>(editor: T): T & ReactEditor & HistoryEditor {
    const e = editor as T & ReactEditor & HistoryEditor;
    const { insertData } = e;

    e.insertData = (data: DataTransfer) => {
        const html = data.getData('text/html');

        if (html?.includes('<table')) {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const tableElements = parsed.querySelectorAll('table');

            if (tableElements.length > 0) {
                try {
                    // ✅ FIX: Check if rows are selected - if so, paste starting from first selected row
                    if ((e as any).selectedRows?.length > 0) {
                        const selectedRow = (e as any).selectedRows[0];
                        const { tablePath, rowIndex } = selectedRow;

                        // Get the table node
                        const [tableNode] = Editor.node(e, tablePath);

                        // Parse clipboard data
                        const clipboardData = parseTableTo2DArray(tableElements[0] as HTMLTableElement);

                        if (clipboardData.length > 0) {
                            // Use the first cell of the selected row as the starting point
                            const cellPath = [...tablePath, rowIndex, 0];
                            spreadDataIntoTable(e, clipboardData, cellPath, tablePath, tableNode as TableNode);
                            return;
                        }
                    }

                    // ✅ FIX: If there's a multi-cell selection, clear content and spread from first cell
                    if (e.selection && !Range.isCollapsed(e.selection)) {
                        // Get all cells in the selection
                        const selectedCells = Array.from(
                            Editor.nodes(e, {
                                at: e.selection,
                                match: (n) =>
                                    Element.isElement(n) && ((n as any).type === 'table-cell' || (n as any).type === 'table-header'),
                            }),
                        );

                        if (selectedCells.length > 0) {
                            // Get the first cell path
                            const [, firstCellPath] = selectedCells[0];

                            // Find the parent table
                            const tableMatch = Editor.above(e, {
                                at: firstCellPath,
                                match: (n) =>
                                    Element.isElement(n) && ((n as any).type === 'table' || (n as any).type === 'tableWithoutHeaders'),
                            });

                            if (tableMatch) {
                                const [tableNode, tablePath] = tableMatch;

                                // Parse clipboard data
                                const clipboardData = parseTableTo2DArray(tableElements[0] as HTMLTableElement);

                                if (clipboardData.length > 0) {
                                    // Spread data starting from the first selected cell
                                    spreadDataIntoTable(e, clipboardData, firstCellPath, tablePath, tableNode as TableNode);
                                    return;
                                }
                            }
                        }
                    }

                    // Check if cursor is inside an existing table cell
                    const cellMatch = Editor.above(e, {
                        match: (n) => Element.isElement(n) && ((n as any).type === 'table-cell' || (n as any).type === 'table-header'),
                    });

                    if (!cellMatch) {
                        // Cursor is NOT inside a table - insert new table(s)
                        const tableNodes = Array.from(tableElements).map((tableElement) =>
                            createTableNodeFromDom(tableElement as HTMLTableElement),
                        );

                        Transforms.insertNodes(e, tableNodes);
                        return;
                    }

                    // Cursor is inside a table cell - spread data across cells
                    const [cellNode, cellPath] = cellMatch;

                    // Find the parent table
                    const tableMatch = Editor.above(e, {
                        match: (n) => Element.isElement(n) && ((n as any).type === 'table' || (n as any).type === 'tableWithoutHeaders'),
                    });

                    if (!tableMatch) {
                        // No parent table found, fallback to default behavior
                        insertData(data);
                        return;
                    }

                    const [tableNode, tablePath] = tableMatch;

                    // Parse the first clipboard table into 2D array
                    const clipboardData = parseTableTo2DArray(tableElements[0] as HTMLTableElement);

                    if (clipboardData.length === 0) {
                        // No data to paste, fallback to default behavior
                        insertData(data);
                        return;
                    }

                    spreadDataIntoTable(e, clipboardData, cellPath, tablePath, tableNode as TableNode);
                    return;
                } catch (error) {
                    console.error('Failed to process pasted table:', error);
                    // Fallback to default paste behavior
                    insertData(data);
                    return;
                }
            }
        }

        // Fallback if no table is found
        insertData(data);
    };

    return e;
}

export default withExcelPaste;
